import React from 'react'
import { motion } from 'framer-motion'
import { useParams, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  ArrowLeft, 
  MessageSquare, 
  Heart, 
  Share2, 
  Play, 
  Users, 
  Bell, 
  Send, 
  Image, 
  Video, 
  Smile, 
  Eye, 
  TrendingUp, 
  Zap, 
  Globe, 
  Shield, 
  MessageCircle, 
  ThumbsUp, 
  Star, 
  Upload,
  Search,
  Filter,
  Calendar,
  Building
} from 'lucide-react'

export const SocialEnterpriseFeatureDetailPage: React.FC = () => {
  const { featureId } = useParams()
  const { t } = useTranslation(['workforce', 'social'])

  if (!featureId || featureId !== 'social') {
    return <Navigate to="/workforce" replace />
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <a 
            href="/workforce" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
          </a>
          
          <div className="text-center">
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", bounce: 0.5 }}
              className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-full mb-8 shadow-lg"
            >
              <MessageSquare className="w-7 h-7" />
              <span className="font-bold text-lg">{t('features.socialEnterprise.title')}</span>
            </motion.div>
            <h1 className="text-6xl font-extrabold bg-gradient-to-r from-purple-600 via-blue-600 to-pink-600 bg-clip-text text-transparent mb-8">
              {t('features.socialEnterprise.title')}
            </h1>
            <p className="text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              {t('features.socialEnterprise.description')}
            </p>
          </div>
        </motion.div>

        {/* Hero Interactive Feed */}
        <motion.section
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="mb-24"
        >
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/50">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-3 rounded-full mb-6">
                <Globe className="w-6 h-6" />
                <span className="font-semibold">{t('social:social.feedSection.title')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('social:social.feedSection.subtitle')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('social:social.feedSection.description')}
              </p>
            </div>

            {/* Mock Social Feed */}
            <div className="max-w-2xl mx-auto">
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="space-y-6"
              >
                {/* Create Post Area */}
                <motion.div variants={cardVariants} className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white font-bold">
                      JG
                    </div>
                    <div className="flex-1">
                      <div className="bg-white rounded-full px-4 py-3 text-gray-500 cursor-pointer">
                        {t('social:social.feedSection.createPost.placeholder')}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 pl-16">
                    <button className="flex items-center gap-2 text-green-600 hover:text-green-700 text-sm font-medium">
                      <Image className="w-4 h-4" />
                      {t('social:social.feedSection.createPost.photo')}
                    </button>
                    <button className="flex items-center gap-2 text-red-600 hover:text-red-700 text-sm font-medium">
                      <Video className="w-4 h-4" />
                      {t('social:social.feedSection.createPost.youtube')}
                    </button>
                    <button className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium">
                      <Smile className="w-4 h-4" />
                      {t('social:social.feedSection.createPost.emoji')}
                    </button>
                  </div>
                </motion.div>

                {/* Sample Posts */}
                <motion.div variants={cardVariants} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {t('social:social.feedSection.samplePosts.first.authorInitials')}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{t('social:social.feedSection.samplePosts.first.authorName')}</div>
                      <div className="text-sm text-gray-500 flex items-center gap-2">
                        <Building className="w-3 h-3" />
                        {t('social:social.feedSection.samplePosts.first.department')} • {t('social:social.feedSection.samplePosts.first.timeAgo')}
                      </div>
                    </div>
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  </div>
                  
                  <p className="text-gray-800 mb-4 leading-relaxed">
                    {t('social:social.feedSection.samplePosts.first.content')}
                  </p>
                  
                  <div className="bg-gray-900 rounded-xl aspect-video flex items-center justify-center mb-4 relative overflow-hidden">
                    <Play className="w-12 h-12 text-white z-10" />
                    <div className="absolute inset-0 bg-gradient-to-r from-red-600/80 to-red-500/80"></div>
                    <div className="absolute bottom-3 left-3 text-white text-sm font-medium">
                      {t('social:social.feedSection.samplePosts.first.videoTitle')}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500 border-t border-gray-100 pt-4">
                    <div className="flex items-center gap-6">
                      <button className="flex items-center gap-2 text-red-500 hover:text-red-600 transition-colors">
                        <Heart className="w-4 h-4 fill-current" />
                        <span>{t('social:social.feedSection.samplePosts.first.likes')}</span>
                      </button>
                      <button className="flex items-center gap-2 text-blue-500 hover:text-blue-600 transition-colors">
                        <MessageCircle className="w-4 h-4" />
                        <span>{t('social:social.feedSection.samplePosts.first.comments')}</span>
                      </button>
                      <button className="flex items-center gap-2 text-green-500 hover:text-green-600 transition-colors">
                        <Share2 className="w-4 h-4" />
                        <span>{t('social:social.feedSection.samplePosts.first.shares')}</span>
                      </button>
                    </div>
                    <div className="text-xs">
                      <Eye className="w-3 h-3 inline mr-1" />
                      {t('social:social.feedSection.samplePosts.first.views')}
                    </div>
                  </div>
                </motion.div>

                <motion.div variants={cardVariants} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {t('social:social.feedSection.samplePosts.second.authorInitials')}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{t('social:social.feedSection.samplePosts.second.authorName')}</div>
                      <div className="text-sm text-gray-500 flex items-center gap-2">
                        <Building className="w-3 h-3" />
                        {t('social:social.feedSection.samplePosts.second.department')} • {t('social:social.feedSection.samplePosts.second.timeAgo')}
                      </div>
                    </div>
                    <div className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs font-medium">
                      {t('social:social.feedSection.samplePosts.second.pinned')}
                    </div>
                  </div>
                  
                  <p className="text-gray-800 mb-4 leading-relaxed">
                    {t('social:social.feedSection.samplePosts.second.content')}
                  </p>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500 border-t border-gray-100 pt-4">
                    <div className="flex items-center gap-6">
                      <button className="flex items-center gap-2 text-red-500 hover:text-red-600 transition-colors">
                        <Heart className="w-4 h-4 fill-current" />
                        <span>{t('social:social.feedSection.samplePosts.second.likes')}</span>
                      </button>
                      <button className="flex items-center gap-2 text-blue-500 hover:text-blue-600 transition-colors">
                        <MessageCircle className="w-4 h-4" />
                        <span>{t('social:social.feedSection.samplePosts.second.comments')}</span>
                      </button>
                      <button className="flex items-center gap-2 text-green-500 hover:text-green-600 transition-colors">
                        <Share2 className="w-4 h-4" />
                        <span>{t('social:social.feedSection.samplePosts.second.shares')}</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Chat em Tempo Real */}
        <motion.section
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-24"
        >
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-3xl p-10 shadow-2xl text-white overflow-hidden relative">
            <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-48 translate-x-48"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl translate-y-48 -translate-x-48"></div>
            
            <div className="relative z-10">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur px-6 py-3 rounded-full mb-6">
                  <Zap className="w-6 h-6" />
                  <span className="font-semibold">{t('social:social.chatSection.title')}</span>
                </div>
                <h2 className="text-5xl font-bold mb-6">
                  {t('social:social.chatSection.headline')}
                </h2>
                <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                  {t('social:social.chatSection.description')}
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="space-y-8">
                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                  >
                    <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                      <MessageCircle className="w-7 h-7" />
                      {t('social:social.chatSection.smartChat.title')}
                    </h3>
                    <ul className="space-y-3">
                      <li className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-green-400 rounded-full flex items-center justify-center">
                          <Send className="w-3 h-3 text-green-900" />
                        </div>
                        <span>{t('social:social.chatSection.smartChat.features.websocket')}</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center">
                          <Users className="w-3 h-3 text-blue-900" />
                        </div>
                        <span>{t('social:social.chatSection.smartChat.features.groups')}</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-purple-400 rounded-full flex items-center justify-center">
                          <Image className="w-3 h-3 text-purple-900" />
                        </div>
                        <span>{t('social:social.chatSection.smartChat.features.uploads')}</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-pink-400 rounded-full flex items-center justify-center">
                          <Bell className="w-3 h-3 text-pink-900" />
                        </div>
                        <span>{t('social:social.chatSection.smartChat.features.notifications')}</span>
                      </li>
                    </ul>
                  </motion.div>

                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                  >
                    <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                      <Shield className="w-7 h-7" />
                      {t('social:social.chatSection.security.title')}
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-white mb-1">{t('social:social.chatSection.security.encryption')}</div>
                        <div className="text-sm text-blue-200">{t('social:social.chatSection.security.encryptionLabel')}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-white mb-1">{t('social:social.chatSection.security.monitoring')}</div>
                        <div className="text-sm text-blue-200">{t('social:social.chatSection.security.monitoringLabel')}</div>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Mock Chat Interface */}
                <div className="relative">
                  <motion.div 
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7 }}
                    className="bg-white rounded-3xl p-6 shadow-2xl"
                  >
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                      <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"></div>
                      <div>
                        <div className="font-semibold text-gray-900">{t('social:social.chatSection.mockChat.groupName')}</div>
                        <div className="text-sm text-green-600 flex items-center gap-1">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          {t('social:social.chatSection.mockChat.onlineStatus')}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4 mb-6">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">M</div>
                        <div className="flex-1">
                          <div className="bg-gray-100 rounded-2xl px-4 py-2 max-w-xs">
                            <div className="text-sm">{t('social:social.chatSection.mockChat.messages.first.content')}</div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">{t('social:social.chatSection.mockChat.messages.first.author')} • {t('social:social.chatSection.mockChat.messages.first.time')}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3 justify-end">
                        <div className="flex-1">
                          <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl px-4 py-2 max-w-xs ml-auto">
                            <div className="text-sm">{t('social:social.chatSection.mockChat.messages.second.content')}</div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1 text-right">{t('social:social.chatSection.mockChat.messages.second.author')} • {t('social:social.chatSection.mockChat.messages.second.time')}</div>
                        </div>
                        <div className="w-6 h-6 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white text-xs font-bold">JG</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-2xl">
                      <Smile className="w-5 h-5 text-gray-400" />
                      <input 
                        className="flex-1 bg-transparent text-sm placeholder-gray-500 outline-none"
                        placeholder={t('social:social.chatSection.mockChat.inputPlaceholder')}
                      />
                      <Image className="w-5 h-5 text-gray-400" />
                      <Send className="w-5 h-5 text-purple-600" />
                    </div>
                  </motion.div>
                  
                  {/* Floating notification */}
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.2 }}
                    className="absolute -top-4 -right-4 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg"
                  >
                    <Bell className="w-4 h-4 inline mr-2" />
                    {t('social:social.chatSection.mockChat.notification')}
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* YouTube Integration */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mb-24"
        >
          <div className="bg-gradient-to-r from-red-600 to-pink-600 rounded-3xl p-10 shadow-2xl text-white">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur px-6 py-3 rounded-full mb-6">
                <Video className="w-6 h-6" />
                <span className="font-semibold">{t('social:social.youtubeSection.title')}</span>
              </div>
              <h2 className="text-5xl font-bold mb-6">
                {t('social:social.youtubeSection.headline')}
              </h2>
              <p className="text-xl text-red-100 max-w-3xl mx-auto">
                {t('social:social.youtubeSection.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <motion.div 
                whileHover={{ y: -10 }}
                className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
              >
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <Play className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-3">{t('social:social.youtubeSection.features.player.title')}</h3>
                <p className="text-red-100 text-sm">
                  {t('social:social.youtubeSection.features.player.description')}
                </p>
              </motion.div>

              <motion.div 
                whileHover={{ y: -10 }}
                className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
              >
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <Search className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-3">{t('social:social.youtubeSection.features.detection.title')}</h3>
                <p className="text-red-100 text-sm">
                  {t('social:social.youtubeSection.features.detection.description')}
                </p>
              </motion.div>

              <motion.div 
                whileHover={{ y: -10 }}
                className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
              >
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <Eye className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-3">{t('social:social.youtubeSection.features.preview.title')}</h3>
                <p className="text-red-100 text-sm">
                  {t('social:social.youtubeSection.features.preview.description')}
                </p>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Sistema de Avisos */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="mb-24"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-3 bg-amber-100 text-amber-800 px-6 py-3 rounded-full mb-6">
                <Bell className="w-6 h-6" />
                <span className="font-semibold">{t('social:social.noticeSystem.title')}</span>
              </div>
              <h2 className="text-5xl font-bold text-gray-900 mb-6">
                {t('social:social.noticeSystem.headline')}
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {t('social:social.noticeSystem.description')}
              </p>

              <div className="space-y-6">
                <motion.div 
                  whileHover={{ x: 10 }}
                  className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-red-500"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                      <Bell className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">{t('social:social.noticeSystem.notices.urgent.level')}</div>
                      <div className="text-sm text-gray-500">{t('social:social.noticeSystem.notices.urgent.department')}</div>
                    </div>
                  </div>
                  <p className="text-gray-800">{t('social:social.noticeSystem.notices.urgent.content')}</p>
                </motion.div>

                <motion.div 
                  whileHover={{ x: 10 }}
                  className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-amber-500"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                      <Star className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">{t('social:social.noticeSystem.notices.important.level')}</div>
                      <div className="text-sm text-gray-500">{t('social:social.noticeSystem.notices.important.department')}</div>
                    </div>
                  </div>
                  <p className="text-gray-800">{t('social:social.noticeSystem.notices.important.content')}</p>
                </motion.div>

                <motion.div 
                  whileHover={{ x: 10 }}
                  className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-blue-500"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <Calendar className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">{t('social:social.noticeSystem.notices.event.level')}</div>
                      <div className="text-sm text-gray-500">{t('social:social.noticeSystem.notices.event.department')}</div>
                    </div>
                  </div>
                  <p className="text-gray-800">{t('social:social.noticeSystem.notices.event.content')}</p>
                </motion.div>
              </div>
            </div>

            <div className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.2 }}
                className="bg-white rounded-3xl p-8 shadow-2xl"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">{t('social:social.noticeSystem.management.title')}</h3>
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-gray-400" />
                    <Search className="w-4 h-4 text-gray-400" />
                  </div>
                </div>

                <div className="space-y-4">
                  <motion.div 
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.4 }}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span className="font-medium text-gray-900">{t('social:social.noticeSystem.management.filter.critical.level')}</span>
                    </div>
                    <div className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-bold">
                      {t('social:social.noticeSystem.management.filter.critical.count')}
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.5 }}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                      <span className="font-medium text-gray-900">{t('social:social.noticeSystem.management.filter.important.level')}</span>
                    </div>
                    <div className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-bold">
                      {t('social:social.noticeSystem.management.filter.important.count')}
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.6 }}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <span className="font-medium text-gray-900">{t('social:social.noticeSystem.management.filter.informative.level')}</span>
                    </div>
                    <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-bold">
                      {t('social:social.noticeSystem.management.filter.informative.count')}
                    </div>
                  </motion.div>
                </div>

                <button className="w-full mt-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold py-3 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-colors">
                  {t('social:social.noticeSystem.management.button')}
                </button>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Comunicação Departamental */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="mb-24"
        >
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-3 bg-green-100 text-green-800 px-6 py-3 rounded-full mb-6">
                <Building className="w-6 h-6" />
                <span className="font-semibold">{t('social:social.departmentalComm.title')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {t('social:social.departmentalComm.headline')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('social:social.departmentalComm.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200"
              >
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-4">
                  <ThumbsUp className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('social:social.departmentalComm.sections.tips.title')}</h3>
                <p className="text-gray-600 mb-4">{t('social:social.departmentalComm.sections.tips.description')}</p>
                <div className="bg-white rounded-lg p-3">
                  <div className="text-sm font-medium text-gray-900 mb-1">{t('social:social.departmentalComm.sections.tips.latest')}</div>
                  <div className="text-xs text-gray-600">{t('social:social.departmentalComm.sections.tips.example')}</div>
                </div>
              </motion.div>

              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-red-50 to-rose-50 rounded-2xl p-6 border border-red-200"
              >
                <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center mb-4">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('social:social.departmentalComm.sections.complaints.title')}</h3>
                <p className="text-gray-600 mb-4">{t('social:social.departmentalComm.sections.complaints.description')}</p>
                <div className="bg-white rounded-lg p-3">
                  <div className="text-sm font-medium text-gray-900 mb-1">{t('social:social.departmentalComm.sections.complaints.status')}</div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                    <div className="text-xs text-gray-600">{t('social:social.departmentalComm.sections.complaints.pending')}</div>
                  </div>
                </div>
              </motion.div>

              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-200"
              >
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-4">
                  <Star className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('social:social.departmentalComm.sections.suggestions.title')}</h3>
                <p className="text-gray-600 mb-4">{t('social:social.departmentalComm.sections.suggestions.description')}</p>
                <div className="bg-white rounded-lg p-3">
                  <div className="text-sm font-medium text-gray-900 mb-1">{t('social:social.departmentalComm.sections.suggestions.reviewing')}</div>
                  <div className="text-xs text-gray-600">{t('social:social.departmentalComm.sections.suggestions.example')}</div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Analytics e Métricas */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4 }}
          className="mb-24"
        >
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-3 bg-cyan-100 text-cyan-800 px-6 py-3 rounded-full mb-6">
                <TrendingUp className="w-6 h-6" />
                <span className="font-semibold">{t('social:social.analytics.title')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {t('social:social.analytics.headline')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('social:social.analytics.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-6 border border-red-200"
              >
                <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center mb-4">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <div className="text-3xl font-bold text-red-700 mb-1">{t('social:social.analytics.metrics.likes.value')}</div>
                <div className="text-sm text-gray-600 mb-2">{t('social:social.analytics.metrics.likes.title')}</div>
                <div className="text-xs text-red-600 font-semibold">{t('social:social.analytics.metrics.likes.increase')}</div>
              </motion.div>

              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200"
              >
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-4">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <div className="text-3xl font-bold text-blue-700 mb-1">{t('social:social.analytics.metrics.comments.value')}</div>
                <div className="text-sm text-gray-600 mb-2">{t('social:social.analytics.metrics.comments.title')}</div>
                <div className="text-xs text-blue-600 font-semibold">{t('social:social.analytics.metrics.comments.increase')}</div>
              </motion.div>

              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border border-green-200"
              >
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-4">
                  <Share2 className="w-6 h-6 text-white" />
                </div>
                <div className="text-3xl font-bold text-green-700 mb-1">{t('social:social.analytics.metrics.shares.value')}</div>
                <div className="text-sm text-gray-600 mb-2">{t('social:social.analytics.metrics.shares.title')}</div>
                <div className="text-xs text-green-600 font-semibold">{t('social:social.analytics.metrics.shares.increase')}</div>
              </motion.div>

              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 border border-purple-200"
              >
                <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mb-4">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <div className="text-3xl font-bold text-purple-700 mb-1">{t('social:social.analytics.metrics.views.value')}</div>
                <div className="text-sm text-gray-600 mb-2">{t('social:social.analytics.metrics.views.title')}</div>
                <div className="text-xs text-purple-600 font-semibold">{t('social:social.analytics.metrics.views.increase')}</div>
              </motion.div>
            </div>

            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl p-8 border border-cyan-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                {t('social:social.analytics.activeDepartments.title')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 text-center">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg mx-auto mb-2"></div>
                  <div className="font-semibold text-gray-900">{t('social:social.analytics.activeDepartments.departments.reception.name')}</div>
                  <div className="text-2xl font-bold text-gray-700 my-1">{t('social:social.analytics.activeDepartments.departments.reception.count')}</div>
                  <div className="text-xs text-gray-500">{t('social:social.analytics.activeDepartments.postCount')}</div>
                </div>
                <div className="bg-white rounded-xl p-4 text-center">
                  <div className="w-8 h-8 bg-green-500 rounded-lg mx-auto mb-2"></div>
                  <div className="font-semibold text-gray-900">{t('social:social.analytics.activeDepartments.departments.housekeeping.name')}</div>
                  <div className="text-2xl font-bold text-gray-700 my-1">{t('social:social.analytics.activeDepartments.departments.housekeeping.count')}</div>
                  <div className="text-xs text-gray-500">{t('social:social.analytics.activeDepartments.postCount')}</div>
                </div>
                <div className="bg-white rounded-xl p-4 text-center">
                  <div className="w-8 h-8 bg-orange-500 rounded-lg mx-auto mb-2"></div>
                  <div className="font-semibold text-gray-900">{t('social:social.analytics.activeDepartments.departments.fnb.name')}</div>
                  <div className="text-2xl font-bold text-gray-700 my-1">{t('social:social.analytics.activeDepartments.departments.fnb.count')}</div>
                  <div className="text-xs text-gray-500">{t('social:social.analytics.activeDepartments.postCount')}</div>
                </div>
                <div className="bg-white rounded-xl p-4 text-center">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg mx-auto mb-2"></div>
                  <div className="font-semibold text-gray-900">{t('social:social.analytics.activeDepartments.departments.maintenance.name')}</div>
                  <div className="text-2xl font-bold text-gray-700 my-1">{t('social:social.analytics.activeDepartments.departments.maintenance.count')}</div>
                  <div className="text-xs text-gray-500">{t('social:social.analytics.activeDepartments.postCount')}</div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Features Técnicas */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6 }}
          className="mb-24"
        >
          <div className="bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 rounded-3xl p-10 shadow-2xl text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl"></div>
            
            <div className="relative z-10">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur px-6 py-3 rounded-full mb-6">
                  <Zap className="w-6 h-6" />
                  <span className="font-semibold">{t('social:social.technicalSpecs.title')}</span>
                </div>
                <h2 className="text-4xl font-bold mb-6">
                  {t('social:social.technicalSpecs.headline')}
                </h2>
                <p className="text-xl text-purple-100 max-w-2xl mx-auto">
                  {t('social:social.technicalSpecs.description')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <Globe className="w-8 h-8 mb-4 text-cyan-400" />
                  <h3 className="text-lg font-bold mb-3">{t('social:social.technicalSpecs.features.websocket.title')}</h3>
                  <ul className="space-y-1 text-sm text-purple-100">
                    {(t('social:social.technicalSpecs.features.websocket.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <Upload className="w-8 h-8 mb-4 text-green-400" />
                  <h3 className="text-lg font-bold mb-3">{t('social:social.technicalSpecs.features.upload.title')}</h3>
                  <ul className="space-y-1 text-sm text-purple-100">
                    {(t('social:social.technicalSpecs.features.upload.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <Shield className="w-8 h-8 mb-4 text-amber-400" />
                  <h3 className="text-lg font-bold mb-3">{t('social:social.technicalSpecs.features.security.title')}</h3>
                  <ul className="space-y-1 text-sm text-purple-100">
                    {(t('social:social.technicalSpecs.features.security.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <TrendingUp className="w-8 h-8 mb-4 text-pink-400" />
                  <h3 className="text-lg font-bold mb-3">{t('social:social.technicalSpecs.features.performance.title')}</h3>
                  <ul className="space-y-1 text-sm text-purple-100">
                    {(t('social:social.technicalSpecs.features.performance.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.section>

      </div>
    </div>
  )
}

export default SocialEnterpriseFeatureDetailPage